import { useEffect, useRef, useCallback, useState } from 'react';

type DelayType = number | number[];

interface UseTimedFunctionReturn {
  start: () => void;
  cancel: () => void;
  restart: () => void;
  isRunning: boolean;
}

export function useTimedFunction(
  callback: () => Promise<void> | void,
  options: {
    times: number,
    delayInSeconds: DelayType
  }
): UseTimedFunctionReturn {
  const { times, delayInSeconds } = options;
  const countRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cancelledRef = useRef(false);
  const [isRunning, setIsRunning] = useState(false);

  const clear = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    clear();
    setIsRunning(false);
  }, []);

  const runSequence = useCallback(() => {
    if (times <= 0) return;
    cancelledRef.current = false;
    countRef.current = 0;
    setIsRunning(true);

    const delays: number[] = Array.isArray(delayInSeconds)
      ? delayInSeconds
      : Array.from({ length: times }, (_, i) => delayInSeconds * Math.pow(2, i));

    async function run(index: number) {
      if (cancelledRef.current || index >= times) {
        setIsRunning(false);
        return;
      }

      timeoutRef.current = setTimeout(async () => {
        if (cancelledRef.current) return;

        await callback();
        countRef.current += 1;
        run(index + 1);
      }, delays[index] * 1000);
    }

    run(0);
  }, [callback, delayInSeconds, times]);

  const start = useCallback(() => {
    cancel();     
    runSequence(); 
  }, [cancel, runSequence]);

  const restart = useCallback(() => {
    start();
  }, [start]);

  useEffect(() => {
    return cancel;
  }, [cancel]);

  return { start, cancel, restart, isRunning };
}
